<template>
	<div ref="sectionRef" class="top-teacher" :class="{ 'top-teacher--en': locale === 'en' }">
		<div class="title">
			<img src="../../../images/pc/left2.png" class="pic" alt="" />
			{{ t("index.topTeacher.title") }}
			<img src="../../../images/pc/right2.png" class="pic" alt="" />
		</div>
		<div class="title2">{{ t("index.topTeacher.title2") }}</div>

		<div v-if="teachers.length" class="content">
			<div class="teacher-tabs">
				<button
					v-if="showPager"
					class="teacher-tabs__nav"
					type="button"
					aria-label="Previous teacher"
					:disabled="!canGoPrev"
					@click="handlePrev"
				>
					<img :src="iconLeft" alt="" aria-hidden="true" />
				</button>

				<div class="teacher-tabs__list">
					<button
						v-for="{ teacher, index } in visibleTeachers"
						:key="`${teacher.uid}-${index}`"
						type="button"
						class="teacher-tabs__item"
						:class="{ 'teacher-tabs__item--active': selectedIndex === index }"
						@click="handleSelectTeacher(teacher, index)"
					>
						<div class="teacher-tabs__avatar">
							<img
								v-if="teacher.avatar"
								:src="teacher.avatar"
								:alt="teacher.nickname"
								class="teacher-tabs__avatar-img"
							/>
						</div>
						<div class="teacher-tabs__info">
							<span class="teacher-tabs__name">{{ teacher.nickname }}</span>
							<span class="teacher-tabs__desc">{{ teacher.tagText }}</span>
						</div>
					</button>
				</div>

				<button
					v-if="showPager"
					class="teacher-tabs__nav"
					type="button"
					aria-label="Next teacher"
					:disabled="!canGoNext"
					@click="handleNext"
				>
					<img :src="iconRight" alt="" aria-hidden="true" />
				</button>
			</div>

			<div v-if="currentTeacher" class="teacher-panel">
				<div class="teacher-panel__profile">
					<div class="top">
						<div class="teacher-panel__stats">
							<span class="teacher-panel__stat">
								<img :src="starIcon" alt="" aria-hidden="true" />
								{{ currentTeacher.score }}
							</span>
							<span class="teacher-panel__stat">
								<img :src="numIcon" alt="" aria-hidden="true" />
								{{ currentTeacher.answers }}
							</span>
						</div>

						<div class="teacher-panel__avatar">
							<img
								v-if="currentTeacher.avatar"
								:src="currentTeacher.avatar"
								:alt="currentTeacher.nickname"
								class="teacher-panel__avatar-img"
							/>
						</div>
					</div>

					<div class="bottom">
						<h3 class="teacher-panel__name">{{ currentTeacher.nickname }}</h3>

						<div v-if="currentTeacher.tags.length" class="teacher-panel__tags">
							<span
								v-for="(tag, tagIndex) in currentTeacher.tags"
								:key="`${tag}-${tagIndex}`"
								class="teacher-panel__tag"
							>
								{{ tag }}
							</span>
						</div>

						<p class="teacher-panel__intro">{{ currentTeacher.introduce }}</p>

						<button
							class="teacher-panel__action"
							type="button"
							@click="handleViewProfile(currentTeacher)"
						>
							{{ t("index.topTeacher.viewProfile") }}
							<span aria-hidden="true">&gt;</span>
						</button>
					</div>
				</div>

				<div class="teacher-panel__videos">
					<div class="teacher-panel__video-header">
						<div class="teacher-panel__video-title">
							<img :src="hotIcon" alt="" aria-hidden="true" />
							{{ t("index.topTeacher.videoTitle") }}
						</div>
						<button class="teacher-panel__video-more" type="button" @click="handleViewMoreVideos">
							{{ t("index.topTeacher.viewMoreVideos") }}
							<span aria-hidden="true">&gt;</span>
						</button>
					</div>

					<div v-if="currentVideos.length" class="teacher-panel__video-grid">
						<article
							v-for="(video, index) in currentVideos"
							:key="video.id"
							:ref="el => bindVideoRef(el, video, index, currentTeacher?.uid)"
							class="teacher-panel__video-item"
							role="button"
							tabindex="0"
							@click="handleVideoClick(video, index)"
							@keydown.enter="handleVideoClick(video, index)"
						>
							<div class="teacher-panel__video-cover">
								<img
									v-if="video.cover"
									:src="video.cover"
									:alt="video.title"
									class="teacher-panel__video-cover-img"
								/>
								<img :src="playIcon" alt="" class="teacher-panel__video-play" aria-hidden="true" />
								<span v-if="video.duration" class="teacher-panel__video-duration">
									{{ video.duration }}
								</span>
							</div>
							<h4 class="teacher-panel__video-name">{{ video.title }}</h4>
							<div class="teacher-panel__video-views">
								<img :src="eyeIcon" alt="" aria-hidden="true" />
								<span>{{ formatViewCount(video.viewCount) }}</span>
							</div>
						</article>
					</div>
					<p v-else class="teacher-panel__video-empty">{{ t("index.topTeacher.noVideos") }}</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "~/composables/useI18n";
import { getUserDetail, getSquareCreatorFeatured } from "~/api/api";
import {
	useHomepageTracking,
	useSectionView,
	useVideoCardImpression,
} from "@variants/index/composables/useHomepageTracking";
import starIcon from "../../../images/pc/start.png";
import numIcon from "../../../images/pc/num.png";
import hotIcon from "../../../images/pc/hot.png";
import iconLeft from "../../../images/pc/swiper-left.png";
import iconRight from "../../../images/pc/swiper-right.png";
import eyeIcon from "../../../images/pc/eye.png";
import playIcon from "../../../images/pc/vidoe.png";

const { t, locale } = useI18n();
const { track } = useHomepageTracking("pc");
const sectionRef = ref(null);
const { bindVideoRef } = useVideoCardImpression({ track });

useSectionView(sectionRef, {
	track,
	sectionId: "featured_mentor",
});

const TEACHER_IDS = [
	"175455173334630723",
	"164197918000000150",
	"64435",
	"162156213800000090",
	"174557549192630723",
	"157425565300200060",
	"172421482668630723",
];

const selectedIndex = ref(0);
const currentPage = ref(0);
const PAGE_SIZE = 4;
const VIDEO_SIZE = 4;

const { data: teacherList } = await useLazyAsyncData(
	() => `home-top-teacher-pc-${locale.value}`,
	async () => {
		const results = await Promise.all(
			TEACHER_IDS.map(async uid => {
				try {
					const [userRes, videoRes] = await Promise.all([
						getUserDetail({ uid }),
						getSquareCreatorFeatured({ uid }),
					]);
					const teacher = formatTeacher(userRes?.data || null);
					if (!teacher) return null;

					const horizVideo = videoRes?.data?.horiz_video || [];
					teacher.videos = formatVideos(horizVideo);
					return teacher;
				} catch (error) {
					console.error(`获取导师详情失败 uid=${uid}:`, error);
					return null;
				}
			})
		);

		return results.filter(Boolean);
	},
	{
		default: () => [],
		server: true,
		watch: [locale],
	}
);

const teachers = computed(() => teacherList.value || []);

const showPager = computed(() => teachers.value.length > PAGE_SIZE);

const pageStarts = computed(() => {
	const total = teachers.value.length;
	if (total <= PAGE_SIZE) return [0];

	const maxStart = total - PAGE_SIZE;
	const starts = [0];
	let next = PAGE_SIZE;

	while (next < maxStart) {
		starts.push(next);
		next += PAGE_SIZE;
	}

	if (starts[starts.length - 1] !== maxStart) {
		starts.push(maxStart);
	}

	return starts;
});

const totalPages = computed(() => pageStarts.value.length);

const canGoPrev = computed(() => currentPage.value > 0);

const canGoNext = computed(() => currentPage.value < totalPages.value - 1);

const visibleTeachers = computed(() => {
	const list = teachers.value;
	if (!list.length) return [];

	const start = pageStarts.value[currentPage.value] ?? 0;
	return list.slice(start, start + PAGE_SIZE).map((teacher, slot) => ({
		teacher,
		index: start + slot,
	}));
});

const currentTeacher = computed(() => teachers.value[selectedIndex.value] || null);

const currentVideos = computed(() => (currentTeacher.value?.videos || []).slice(0, VIDEO_SIZE));

watch(
	teachers,
	list => {
		if (!list.length) {
			selectedIndex.value = 0;
			currentPage.value = 0;
			return;
		}
		if (selectedIndex.value >= list.length) {
			selectedIndex.value = 0;
		}
		if (currentPage.value >= totalPages.value) {
			currentPage.value = Math.max(totalPages.value - 1, 0);
		}
	},
	{ immediate: true }
);

function ensureSelectedVisible() {
	const visibleIndices = visibleTeachers.value.map(item => item.index);
	if (!visibleIndices.includes(selectedIndex.value)) {
		selectedIndex.value = visibleIndices[0] ?? 0;
	}
}

function formatTeacher(teacher) {
	if (!teacher) return null;

	const isEn = locale?.value === "en";
	const tags = pickLocalizedTags(teacher, isEn);

	return {
		uid: teacher.uid || "",
		nickname:
			(isEn ? teacher.nickname_en : teacher.nickname) ||
			teacher.nickname ||
			t("index.teacherConsultation.defaultName"),
		avatar: teacher.avatar || "",
		score: formatScore(teacher.score),
		answers: Number(teacher.answers) || 0,
		introduce: pickLocalizedIntroduce(teacher, isEn),
		tags,
		tagText: tags.slice(0, 2).join(" "),
		link: teacher.uid ? `/seer/teacher-detail/${teacher.uid}` : "/seer/master-fortune",
		videos: [],
	};
}

function pickLocalizedIntroduce(teacher, isEn) {
	if (isEn) {
		return (
			teacher.introduce_en ||
			teacher.introduction_en ||
			teacher.creator_introduction_en ||
			teacher.introduce ||
			""
		);
	}
	return teacher.introduce || "";
}

function pickLocalizedTags(teacher, isEn) {
	const zhTags = normalizeTags(teacher.tags || teacher.academic_sector || teacher.label);
	const enTags = normalizeTags(teacher.tags_en || teacher.academic_sector_en || teacher.label_en);
	if (isEn) {
		return enTags.length ? enTags : zhTags;
	}
	return zhTags;
}

function formatVideos(list) {
	return list.slice(0, VIDEO_SIZE).map(item => ({
		id: item.id,
		title: pickLocalizedVideoTitle(item),
		cover: item.cover || "",
		duration: item.media_second_str || "",
		viewCount: Number(item.view_count) || 0,
	}));
}

function pickLocalizedVideoTitle(video) {
	const isEn = locale?.value === "en";
	return (isEn ? video.title_en : video.title) || video.title || "";
}

function formatViewCount(count) {
	return `${count}${t("index.topTeacher.viewUnit")}`;
}

function normalizeTags(source) {
	if (Array.isArray(source)) {
		return source.filter(Boolean).slice(0, 4);
	}
	if (typeof source === "string") {
		return source
			.split(",")
			.map(item => item.trim())
			.filter(Boolean)
			.slice(0, 4);
	}
	return [];
}

function formatScore(value) {
	const score = Number(value);
	if (!score) return "5.0";
	return Math.min(score, 5).toFixed(1);
}

function handlePrev() {
	if (!canGoPrev.value) return;
	track("carousel_arrow_click", {
		section_id: "featured_mentor",
		direction: "left",
	});
	currentPage.value -= 1;
	ensureSelectedVisible();
}

function handleNext() {
	if (!canGoNext.value) return;
	track("carousel_arrow_click", {
		section_id: "featured_mentor",
		direction: "right",
	});
	currentPage.value += 1;
	ensureSelectedVisible();
}

function handleSelectTeacher(teacher, index) {
	track("featured_mentor_select", {
		mentor_id: String(teacher.uid || ""),
		mentor_name: teacher.nickname,
		position: index + 1,
	});
	selectedIndex.value = index;
}

function handleViewProfile(teacher) {
	track("featured_mentor_profile_click", {
		mentor_id: String(teacher.uid || ""),
		mentor_name: teacher.nickname,
	});
	navigateTo(teacher.link || "/seer/master-fortune");
}

function handleViewMoreVideos() {
	track("video_more_click", {
		section_id: "featured_mentor_video",
	});
	if (currentTeacher.value) {
		navigateTo(currentTeacher.value.link || "/seer/master-fortune");
	}
}

function handleVideoClick(video, index) {
	if (!video?.id) return;
	track("video_card_click", {
		content_id: String(video.id),
		content_title: video.title,
		mentor_id: currentTeacher.value?.uid ? String(currentTeacher.value.uid) : undefined,
		position: index + 1,
	});
	navigateTo(`/seer/square-detail/${video.id}`);
}
</script>

<style lang="scss" scoped>
.top-teacher {
	margin-top: 120px;

	.title {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 20px;
		font-size: 32px;
		color: #333333;
		font-weight: bold;

		.pic {
			width: 120px;
			height: 16px;
		}
	}

	.title2 {
		font-size: 24px;
		color: #333333;
		text-align: center;
		margin: 10px 0 80px;
	}

	.teacher-tabs {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		margin-bottom: 20px;

		&__nav {
			flex-shrink: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 50px;
			height: 50px;
			padding: 0;
			border: 0;
			background: transparent;
			cursor: pointer;

			&:disabled {
				cursor: not-allowed;
				opacity: 0.35;
			}

			img {
				width: 100%;
				height: 100%;
			}
		}

		&__list {
			flex: 0 0 auto;
			display: flex;
			justify-content: center;
			gap: 12px;
			min-width: 0;
		}

		&__item {
			flex: 0 0 240px;
			width: 240px;
			max-width: 240px;
			display: flex;
			align-items: flex-start;
			gap: 10px;
			min-width: 0;
			min-height: 74px;
			padding: 12px 14px;
			background: #ffffff;
			border: 1px solid #efefef;
			border-radius: 12px;
			cursor: pointer;
			text-align: left;
			overflow: visible;
			transition:
				background 0.2s,
				border-color 0.2s;

			&--active {
				background: #fef7e8;
				border-color: #f2af19;
			}
		}

		&__avatar {
			flex-shrink: 0;
			width: 50px;
			height: 50px;
			margin-top: 2px;
			border-radius: 50%;
			background: #ffeebe;
			overflow: hidden;
		}

		&__avatar-img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		&__info {
			flex: 1;
			min-width: 0;
			display: flex;
			flex-direction: column;
			gap: 4px;
		}

		&__name {
			display: block;
			font-size: 18px;
			line-height: 22px;
			color: #333333;
			font-weight: normal;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		&__desc {
			display: block;
			font-size: 13px;
			line-height: 18px;
			color: #999999;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	&--en {
		.teacher-tabs__name {
			font-size: 16px;
			line-height: 20px;
		}

		.teacher-tabs__desc {
			font-size: 12px;
			line-height: 16px;
			white-space: normal;
			display: -webkit-box;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
			line-clamp: 2;
			word-break: break-word;
		}
	}

	.teacher-panel {
		display: flex;
		align-items: stretch;
		gap: 32px;
		padding: 32px;
		background: #ffffff;
		border-radius: 20px;
		box-shadow: 0px 4px 9px 0px rgba(0, 0, 0, 0.1);
		margin: 50px 9px;

		&__profile {
			flex: 0 0 432px;
			width: 432px;
			display: flex;
			flex-direction: column;
			align-items: stretch;
			align-self: stretch;
			min-width: 0;
			min-height: 100%;
			border-radius: 20px;

			.top {
				width: 100%;
				background: #fffbf0;
				border-radius: 20px 20px 0 0;
			}

			.bottom {
				flex: 1;
				display: flex;
				flex-direction: column;
				border: 1px solid #fff2d4;
				padding: 30px;
				border-radius: 20px;
			}
		}

		&__stats {
			align-self: flex-start;
			display: flex;
			gap: 8px;
			margin: 16px 0 16px 16px;
		}

		&__stat {
			display: inline-flex;
			align-items: center;
			gap: 4px;
			height: 28px;
			padding: 5px 10px;
			background: #fff8e9;
			border-radius: 8px;
			font-size: 16px;
			line-height: 18px;
			color: #666666;

			img {
				width: 18px;
				height: 18px;
			}
		}

		&__avatar {
			width: 268px;
			height: 268px;
			overflow: hidden;
			margin: 20px auto 0;
		}

		&__avatar-img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		&__name {
			width: 100%;
			margin: 0 0 10px;
			font-size: 28px;
			line-height: 30px;
			color: #333333;
			font-weight: normal;
		}

		&__tags {
			width: 100%;
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
			margin-bottom: 16px;
			margin: 0 0 10px;
		}

		&__tag {
			display: inline-flex;
			align-items: center;
			height: 20px;
			padding: 5px 12px;
			background: #fff8e9;
			border-radius: 8px;
			font-size: 16px;
			line-height: 18px;
			color: #f2af19;
		}

		&__intro {
			width: 100%;
			flex: 1;
			margin: 0 0 20px;
			font-size: 16px;
			line-height: 22px;
			color: #aaaaaa;
			display: -webkit-box;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 6;
			overflow: hidden;
		}

		&__action {
			width: 100%;
			display: inline-flex;
			justify-content: center;
			align-items: center;
			gap: 4px;
			height: 48px;
			padding: 0 24px;
			border: 0;
			border-radius: 8px;
			background: #333333;
			font-size: 20px;
			line-height: 20px;
			color: #ffffff;
			cursor: pointer;

			&:hover {
				opacity: 0.7;
			}
		}

		&__videos {
			flex: 1;
			min-width: 0;
			display: flex;
			flex-direction: column;
			align-self: stretch;
		}

		&__video-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			margin-bottom: 30px;
		}

		&__video-title {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			font-size: 20px;
			color: #333333;
			font-weight: normal;

			img {
				width: 24px;
				height: 24px;
			}
		}

		&__video-more {
			display: inline-flex;
			align-items: center;
			gap: 4px;
			padding: 0;
			border: 0;
			background: transparent;
			font-size: 16px;
			color: #999999;
			cursor: pointer;

			&:disabled {
				cursor: not-allowed;
				opacity: 0.6;
			}
		}

		&__video-grid {
			flex: 1;
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			gap: 40px;
			align-content: center;
		}

		&__video-item {
			min-width: 0;
			padding: 0;
			background: #ffffff;
			border: 1px solid #efefef;
			border-radius: 12px;
			overflow: hidden;
			cursor: pointer;
			transition: box-shadow 0.2s ease;

			&:hover {
				box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
			}
		}

		&__video-cover {
			position: relative;
			width: 100%;
			aspect-ratio: 16 / 9;
			background: #efefef;
			overflow: hidden;
		}

		&__video-cover-img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		&__video-play {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			width: 44px;
			height: 44px;
			object-fit: contain;
			pointer-events: none;
		}

		&__video-duration {
			position: absolute;
			right: 8px;
			bottom: 8px;
			padding: 2px 8px;
			border-radius: 4px;
			background: rgba(0, 0, 0, 0.65);
			font-size: 12px;
			line-height: 18px;
			color: #ffffff;
		}

		&__video-name {
			margin: 10px 12px 8px;
			font-size: 16px;
			line-height: 22px;
			color: #333333;
			font-weight: 500;
			display: -webkit-box;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
			line-clamp: 2;
			overflow: hidden;
			text-overflow: ellipsis;
			word-break: break-word;
		}

		&__video-views {
			display: inline-flex;
			align-items: center;
			gap: 4px;
			margin: 0 12px 12px;
			padding: 4px 10px;
			background: #f4f4f4;
			border-radius: 20px;
			font-size: 14px;
			line-height: 18px;
			color: #666666;

			img {
				width: 14px;
				height: 14px;
			}
		}

		&__video-empty {
			margin: 0;
			padding: 48px 0;
			text-align: center;
			font-size: 16px;
			line-height: 22px;
			color: #999999;
		}
	}
}
</style>
